import {
  faCheck,
  faCircleExclamation,
  faCircleInfo,
  faPaperclip,
} from '@fortawesome/pro-regular-svg-icons'
import {
  Box,
  Text,
  Stack,
  Group,
  TextInput,
  MultiSelect,
  type MultiSelectProps,
  type ComboboxItem,
  type ComboboxLikeRenderOptionInput,
} from '@mantine/core'
import { forwardRef, useEffect, useImperativeHandle } from 'react'

import { useTranslation } from '@flecto/translations'
import {
  type ParameterID,
  type CoreBookingDocument,
  type CoreCustomer,
} from '@flecto/types'
import { Icon, Badge } from '@flecto/ui'
import { RichTextEditor } from '@flecto/ui/@shared/RichTextEditor/RichTextEditor'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'

import { type BookingActionOption } from '../../../booking-actions/hooks/useBookingActions'
import { CustomerEmailInput } from '../../../booking-customer/components/CustomerEmailInput/CustomerEmailInput'
import { useBookingStore } from '../../../booking-details/hooks/useBookingStore'
import {
  type TemplateType,
  useSendEmailForm,
} from '../../../booking-details/hooks/useSendEmailForm'

type EmailForm = {
  subject: string
  message: string
  documents: number[]
  hasUnsignedDocuments: (documentIds: number[]) => number[]
}

type DocumentOption = ComboboxItem & {
  signed_at: string
  value: string
}

export type BookingSendEmailRef = {
  getFormState: () => {
    isValid: boolean
    form: EmailForm
  }
  validateForm: () => void
}

export const BookingSendEmail = forwardRef<
  BookingSendEmailRef,
  {
    bookLink?: string
    template?: BookingActionOption
    document?: CoreBookingDocument
    onDocumentsChange?: (documents: number[]) => void
    onInitialDocuments?: (documents: number[]) => void
  }
>(
  (
    { template, document, bookLink, onDocumentsChange, onInitialDocuments },
    ref
  ) => {
    const { t } = useTranslation('saas', { keyPrefix: 'booking' })
    const { isDesktop } = useMediaQuery()

    const { booking, setBooking, documents } = useBookingStore()
    const formZod = useSendEmailForm({
      template: template as { value: TemplateType } | undefined,
      customer_language: booking?.customer?.locale ?? 'en',
      document,
      bookLink,
    })

    useImperativeHandle(ref, () => ({
      getFormState: () => ({
        isValid: formZod.isValid(),
        form: {
          subject: formZod.values.subject,
          message: formZod.values.message,
          documents: formZod.values.documents as number[],
          hasUnsignedDocuments: formZod.hasUnsignedDocuments,
        },
      }),
      validateForm: () => {
        formZod.validateFields()
      },
    }))

    const getLanguageName = (locale: string | undefined): string => {
      switch (locale) {
        case 'en':
          return `🇬🇧 ${t('send_email.english')}`
        case 'pt':
          return `🇵🇹 ${t('send_email.portuguese')}`
        case 'es':
          return `🇪🇸 ${t('send_email.spanish')}`
        default:
          return ''
      }
    }

    const handleCustomerUpdate = (updatedCustomer: CoreCustomer) => {
      if (booking?.customer && updatedCustomer) {
        setBooking({
          ...booking,
          customer: { ...booking.customer, ...updatedCustomer },
        })
      }
    }

    const renderMultiSelectOption: MultiSelectProps['renderOption'] = (
      item: ComboboxLikeRenderOptionInput<ComboboxItem>
    ) => {
      const { option } = item
      const docOption = option as DocumentOption

      const docOptionValueToNumber= Number(docOption.value)
      const isSelected = formZod.values.documents.includes(docOptionValueToNumber)
      const documentConditions = formZod.checkDocumentBaseConditions(
        docOptionValueToNumber
      )

      return (
        <Group gap='sm' w='100%' align='center' justify='space-between'>
          <Text size='sm'>{docOption.label}</Text>
          <Group gap='xs'>
            {Boolean(documentConditions) && (
              <Badge color='gray.9' c='gray.1' size='sm' display='flex'>
                {docOption.signed_at
                  ? t('send_email.signed')
                  : t('send_email.unsigned')}
              </Badge>
            )}
            <Box ml='sm' miw='md' mah='md'>
              {isSelected && <Icon icon={faCheck} />}
            </Box>
          </Group>
        </Group>
      )
    }

    useEffect(() => {
      onInitialDocuments?.(formZod.initialValues.documents)
    }, [onInitialDocuments])

    return (
      <Box>
        <Stack gap='lg' pb='md'>
          <Group grow>
            <Stack gap='xs'>
              <Group gap={5} align='center'>
                <Text size='md' c='gray.9' fw='500'>
                  {t('send_email.to')}
                </Text>
                <Text size='md' c='red' fw='500'>
                  *
                </Text>
              </Group>
              <Group gap={5} align='center' wrap='nowrap'>
                <Text c='gray.9' fw='500'>
                  {booking?.customer?.full_name}
                </Text>
                <Text c='gray.9' fw='400'>
                  ({booking?.customer?.email ?? t('send_email.no_email')})
                </Text>
              </Group>
              {!booking?.customer?.email && (
                <Group
                  gap={5}
                  align='center'
                  wrap='nowrap'
                  mt='xs'
                  mb='xs'
                  c='red'
                >
                  <Icon icon={faCircleExclamation} />
                  <CustomerEmailInput
                    customerId={booking?.customer?.internal_id as ParameterID}
                    buttonTextColor='red'
                    onUpdateSuccess={handleCustomerUpdate}
                  />
                </Group>
              )}
              {Boolean(booking?.customer?.locale) && (
                <Text c='gray.7'>
                  {t('send_email.email_sent_in', {
                    language: getLanguageName(booking?.customer?.locale),
                  })}
                </Text>
              )}
            </Stack>
          </Group>

          <Group grow>
            <TextInput
              placeholder={t('send_email.quote_ready')}
              value={formZod.values.subject}
              error={formZod.errors.subject}
              label={t('send_email.subject')}
              mt='xs'
              required
              onChange={(e) =>
                formZod.setFieldValue('subject', e.currentTarget.value)
              }
            />
          </Group>

          <Box>
            <RichTextEditor
              withToolbar
              label={t('send_email.message')}
              required
              value={formZod.values.message}
              placeholder={t('send_email.message_placeholder')}
              error={formZod.errors.message}
              onChange={(message) => {
                formZod.setFieldValue(
                  'message',
                  typeof message === 'string' ? message.trim() : ''
                )
              }}
            />
          </Box>

          <Box>
            <MultiSelect
              data={
                documents?.map((document) => ({
                  value: String(document.id),
                  label: document.filename,
                  signed_at: document.signed_at,
                })) ?? []
              }
              value={
                Array.isArray(formZod.values.documents)
                  ? formZod.values.documents.map(String)
                  : []
              }
              onChange={(selectedValues) => {
                const selectedValuesToNumber = selectedValues?.map(Number);
                formZod.setFieldValue('documents', selectedValuesToNumber);
                onDocumentsChange?.(selectedValuesToNumber);
              }}
              renderOption={renderMultiSelectOption}
              onBlur={() => formZod.validateField('documents')}
              label={t('send_email.documents')}
              nothingFoundMessage={t('send_email.nothingFoundMessage')}
              placeholder={t('send_email.attach_documents')}
              leftSection={<Icon icon={faPaperclip} />}
              searchable={isDesktop}
            />
          </Box>
          {Boolean(formZod?.values?.documents?.length) &&
            Boolean(
              formZod.hasUnsignedDocuments(formZod.values.documents)?.length
            ) && (
              <Group gap='sm'>
                <Icon icon={faCircleInfo} color='gray.7' />
                <Group gap='xs'>
                  <Text size='md' c='gray.7'>
                    {t('send_email.signature_request_included')}
                  </Text>
                  <Text size='md' c='gray.7' fw='500'>
                    {t('send_email.unsigned_documents')}
                  </Text>
                </Group>
              </Group>
            )}
        </Stack>
      </Box>
    )
  }
)

BookingSendEmail.displayName = 'BookingSendEmail'
